import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const TermsConditionsContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1541888081622-15cb3a5d89ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Terms Sections
const TermsSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .terms-grid .terms-card:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .terms-grid .terms-card:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .terms-grid .terms-card:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
    .terms-grid .terms-card:nth-child(4) { animation: ${slideInLeft} 0.6s ease-out 0.7s both; }
    .terms-grid .terms-card:nth-child(5) { animation: ${slideInLeft} 0.6s ease-out 0.9s both; }
    .terms-grid .terms-card:nth-child(6) { animation: ${slideInLeft} 0.6s ease-out 1.1s both; }
    .terms-grid .terms-card:nth-child(7) { animation: ${slideInLeft} 0.6s ease-out 1.3s both; }
    .terms-grid .terms-card:nth-child(8) { animation: ${slideInLeft} 0.6s ease-out 1.5s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const TermsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
`;

const TermsCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #0a1628, #1a2744);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 2.5rem;
    color: #0a1628;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #1a2744;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.8;
    font-size: 1.1rem;
    
    margin-bottom: 20px;
  }
  
  ul {
    margin: 20px 0;
    padding-left: 0;
    list-style: none;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      color: #6c757d;
      font-size: 1rem;
      line-height: 1.6;
      
      &::before {
        content: '•';
        color: #0a1628;
        font-weight: bold;
        margin-top: 2px;
      }
    }
  }
  
  .important {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    padding: 20px;
    border-radius: 12px;
    margin: 20px 0;
    border-left: 4px solid #f39c12;
    
    p {
      margin: 0;
      font-weight: 500;
      color: #856404;
    }
  }
  
  .warning {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    padding: 20px;
    border-radius: 12px;
    margin: 20px 0;
    border-left: 4px solid #dc3545;
    
    p {
      margin: 0;
      font-weight: 500;
      color: #721c24;
    }
  }
`;

// Summary Section
const SummarySection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 80px 0;
  border-radius: 30px;
  margin-bottom: 120px;
  
  &.animate-in {
    .summary-points .point:nth-child(1) { animation: ${fadeInUp} 0.6s ease-out 0.1s both; }
    .summary-points .point:nth-child(2) { animation: ${fadeInUp} 0.6s ease-out 0.3s both; }
    .summary-points .point:nth-child(3) { animation: ${fadeInUp} 0.6s ease-out 0.5s both; }
    .summary-points .point:nth-child(4) { animation: ${fadeInUp} 0.6s ease-out 0.7s both; }
  }
`;

const SummaryPoints = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const SummaryPoint = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .icon {
    font-size: 2.5rem;
    color: #0a1628;
    margin-bottom: 20px;
  }
  
  h3 {
    font-size: 1.4rem;
    color: #2c2c2c;
    margin-bottom: 15px;
    font-weight: 500;
  }
  
  p {
    color: #6c757d;
    line-height: 1.6;
    
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: white;
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    backdrop-filter: blur(10px);
    letter-spacing: 0.5px;
    
    &:hover {
      background: white;
      color: #0a1628;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const TermsConditions = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <TermsConditionsContainer>
      <Container>
        <PageTitle>{t('termsConditions.title')}</PageTitle>
        <PageSubtitle>
          {t('termsConditions.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('termsConditions.heroTitle')}</h2>
            <p>
              {t('termsConditions.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <TermsSection 
          id="terms-section" 
          data-animate
          className={animatedSections.has('terms-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('termsConditions.termsTitle')}</SectionTitle>
          <TermsGrid className="terms-grid">
            <TermsCard className="terms-card">
              <div className="icon">📋</div>
              <h3>{t('termsConditions.term1Title')}</h3>
              <p>
                {t('termsConditions.term1Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term1f1')}</li>
                <li>{t('termsConditions.term1f2')}</li>
                <li>{t('termsConditions.term1f3')}</li>
                <li>{t('termsConditions.term1f4')}</li>
              </ul>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">🛒</div>
              <h3>{t('termsConditions.term2Title')}</h3>
              <p>
                {t('termsConditions.term2Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term2f1')}</li>
                <li>{t('termsConditions.term2f2')}</li>
                <li>{t('termsConditions.term2f3')}</li>
                <li>{t('termsConditions.term2f4')}</li>
              </ul>
              <div className="important">
                <p>
                  {t('termsConditions.term2Important')}
                </p>
              </div>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">💳</div>
              <h3>{t('termsConditions.term3Title')}</h3>
              <p>
                {t('termsConditions.term3Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term3f1')}</li>
                <li>{t('termsConditions.term3f2')}</li>
                <li>{t('termsConditions.term3f3')}</li>
                <li>{t('termsConditions.term3f4')}</li>
              </ul>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">🚚</div>
              <h3>{t('termsConditions.term4Title')}</h3>
              <p>
                {t('termsConditions.term4Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term4f1')}</li>
                <li>{t('termsConditions.term4f2')}</li>
                <li>{t('termsConditions.term4f3')}</li>
                <li>{t('termsConditions.term4f4')}</li>
              </ul>
              <div className="warning">
                <p>
                  {t('termsConditions.term4Warning')}
                </p>
              </div>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">🔄</div>
              <h3>{t('termsConditions.term5Title')}</h3>
              <p>
                {t('termsConditions.term5Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term5f1')}</li>
                <li>{t('termsConditions.term5f2')}</li>
                <li>{t('termsConditions.term5f3')}</li>
                <li>{t('termsConditions.term5f4')}</li>
              </ul>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">⚖️</div>
              <h3>{t('termsConditions.term6Title')}</h3>
              <p>
                {t('termsConditions.term6Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term6f1')}</li>
                <li>{t('termsConditions.term6f2')}</li>
                <li>{t('termsConditions.term6f3')}</li>
                <li>{t('termsConditions.term6f4')}</li>
              </ul>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">📞</div>
              <h3>{t('termsConditions.term7Title')}</h3>
              <p>
                {t('termsConditions.term7Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term7f1')}</li>
                <li>{t('termsConditions.term7f2')}</li>
                <li>{t('termsConditions.term7f3')}</li>
                <li>{t('termsConditions.term7f4')}</li>
              </ul>
            </TermsCard>

            <TermsCard className="terms-card">
              <div className="icon">📄</div>
              <h3>{t('termsConditions.term8Title')}</h3>
              <p>
                {t('termsConditions.term8Desc')}
              </p>
              <ul>
                <li>{t('termsConditions.term8f1')}</li>
                <li>{t('termsConditions.term8f2')}</li>
                <li>{t('termsConditions.term8f3')}</li>
                <li>{t('termsConditions.term8f4')}</li>
              </ul>
              <div className="important">
                <p>
                  {t('termsConditions.term8Important')}
                </p>
              </div>
            </TermsCard>
          </TermsGrid>
        </TermsSection>

        <SummarySection 
          id="summary-section" 
          data-animate
          className={animatedSections.has('summary-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('termsConditions.summaryTitle')}</SectionTitle>
          <SummaryPoints className="summary-points">
            <SummaryPoint className="point">
              <div className="icon">✅</div>
              <h3>{t('termsConditions.sum1Title')}</h3>
              <p>{t('termsConditions.sum1Desc')}</p>
            </SummaryPoint>

            <SummaryPoint className="point">
              <div className="icon">🚀</div>
              <h3>{t('termsConditions.sum2Title')}</h3>
              <p>{t('termsConditions.sum2Desc')}</p>
            </SummaryPoint>

            <SummaryPoint className="point">
              <div className="icon">🛡️</div>
              <h3>{t('termsConditions.sum3Title')}</h3>
              <p>{t('termsConditions.sum3Desc')}</p>
            </SummaryPoint>

            <SummaryPoint className="point">
              <div className="icon">📞</div>
              <h3>{t('termsConditions.sum4Title')}</h3>
              <p>{t('termsConditions.sum4Desc')}</p>
            </SummaryPoint>
          </SummaryPoints>
        </SummarySection>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <h2>{t('termsConditions.contactTitle')}</h2>
          <p>
            {t('termsConditions.contactDesc')}
          </p>
          <a href="/iletisim" className="contact-btn">
            {t('termsConditions.contactBtn')}
          </a>
        </ContactSection>
      </Container>
    </TermsConditionsContainer>
  );
};

export default TermsConditions;